// Zezamii products: Products page cards, the dashboard switcher and route gating. Each turns on with its
// moduleFlag (a DefaultSetting); a new one needs that DB row, a CONSTANTS_SETTINGS entry and an entry here.

import openRooms from 'library/helpers/openRooms';
import openPartner from 'library/helpers/openPartner';
import { CONSTANTS_SETTINGS } from './settingsContainer';
import { SETTING_GROUP_IDS } from './settingsMenuConfig';
import {
	LogoAccessIcon,
	LogoBookingsApp,
	LogoLockerApp,
	LogoLockersWithText,
	LogoRoomsApp,
	LogoVisionApp,
	LogoVisitorHubIcon
} from 'assets/images/svg/Logos';
import { ZezamiiIcon } from 'zComponents/atoms/ZezamiiIcon';

/**
 * @typedef {Object} ProductConfig
 * @property {string}   key              - Stable identifier (also the value stored in state.App.dashboard.selected)
 * @property {string}   label            - Display name in UI (e.g. "Zezamii Access")
 * @property {string}   category         - Short kind shown under the name on Settings cards
 * @property {number}   [settingsGroupID] - SettingGroup of its Settings tab, when it has one
 * @property {string}   description      - Short pitch shown on cards
 * @property {JSX}      logo             - Rendered icon/logo element
 * @property {number}   moduleFlag       - DefaultSettingID that toggles this product (operational "Enabled")
 * @property {string}   [catalogCode]    - Billing catalog Code that gates this product ("Entitled").
 *   Module availability = Entitled AND Enabled. Entitlement gating is grandfather-safe: it only
 *   enforces once the org is on billing (see Redux/billing/selectors.js).
 * @property {string[]} [catalogCodesAny] - Alternative to catalogCode for a TIERED product: entitled
 *   when ANY of these codes is available (e.g. Vision → [CAMERAS, VISION, VISION_AI]).
 * @property {'internal'|'external'} kind
 *   - 'internal': lives inside this SPA — switching navigates via react-router
 *   - 'external': lives at a different URL — switching invokes `open()` instead
 * @property {string}   [route]          - Required when kind === 'internal'
 * @property {() => void} [open]         - Required when kind === 'external'
 * @property {string}   [dashboardKey]   - For a product that renders INSIDE another dashboard's
 *   shell rather than owning one itself (e.g. Visitor Management inside Access): the shell to
 *   switch to. Falls back to `key` when absent — every existing product owns its own shell.
 * @property {boolean}  [inSwitcher]     - Default true. Set false to keep this product OFF the
 *   dashboard switcher (it isn't a separate dashboard to switch into) while it still appears as a
 *   toggle card on Settings > Products. See ProductSwitcher's SWITCHABLE_PRODUCTS filter.
 */

/** @type {ProductConfig[]} */
export const PRODUCTS = [
	{
		key: 'access',
		settingsGroupID: SETTING_GROUP_IDS.ACCESS,
		label: 'Access',
		category: 'Locks and credentials',
		description: 'Control every lock and credential. Unlock from the app.',
		logo: <LogoAccessIcon width={40} height={40} />,
		moduleFlag: CONSTANTS_SETTINGS.ACCESS_MODULE,
		catalogCode: 'ACCESS',
		kind: 'internal',
		route: '/access'
	},
	{
		// key matches the registered flag code exactly ('visitorManagement', not e.g. 'visitors') —
		// selectActiveModules resolves the dark-launch flag via `resolveFeatureFromMap(ffMap, ffLoaded,
		// m.key, true)`, so a key that doesn't match the real flag code silently falls back to the
		// grandfather-safe default (flag-enabled) regardless of the actual flag state. Same convention
		// the 'bookings' entry follows for the same reason.
		key: 'visitorManagement',
		label: 'Visitor Management',
		category: 'Visitors',
		description: 'Invite, check in and check out visitors.',
		logo: <LogoVisitorHubIcon width={40} height={40} />,
		moduleFlag: CONSTANTS_SETTINGS.VISITOR_MANAGEMENT_MODULE,
		catalogCode: 'VISITOR_MANAGEMENT',
		kind: 'internal',
		route: '/access/visitors',
		// Lives inside the Access dashboard shell, not its own — see the ProductConfig typedef.
		dashboardKey: 'access',
		inSwitcher: false
	},
	{
		key: 'lockers',
		settingsGroupID: SETTING_GROUP_IDS.ZEZAMII_LOCKER,
		label: 'Lockers',
		category: 'Shared storage',
		description: 'People book a locker and open it from the app.',
		logo: <LogoLockerApp width={40} height={40} />,
		logoWithText: <LogoLockersWithText />,
		moduleFlag: CONSTANTS_SETTINGS.LOCKER_MODULE,
		catalogCode: 'LOCKERS',
		kind: 'internal',
		route: '/lockers'
	},
	{
		key: 'rooms',
		label: 'Rooms',
		category: 'Guest rooms',
		description: 'Guests get a code for their booking.',
		logo: <LogoRoomsApp width={40} height={40} />,
		moduleFlag: CONSTANTS_SETTINGS.ROOMS_MODULE,
		catalogCode: 'ROOMS',
		kind: 'external',
		open: openRooms
	},
	{
		key: 'bookings',
		label: 'Bookings',
		category: 'Desks and spaces',
		description: 'Book desks, rooms and spaces, then unlock for your slot.',
		logo: <LogoBookingsApp width={40} height={40} />,
		moduleFlag: CONSTANTS_SETTINGS.BOOKINGS_MODULE,
		catalogCode: 'BOOKINGS',
		kind: 'internal',
		route: '/bookings'
	},
	{
		key: 'vision',
		label: 'Vision',
		category: 'Cameras',
		description: 'All your cameras in one live view.',
		logo: <LogoVisionApp width={40} height={40} />,
		// The Vision tile is #121210 — 1.03:1 on the black Access shell, so it vanished and the mark
		// read as a floating eye next to four solid product tiles. `invertOnDark` flips the tile and
		// the sclera on dark surfaces; the artwork is already correct on light ones.
		invertOnDark: true,
		moduleFlag: CONSTANTS_SETTINGS.VISION_MODULE,
		// Entitlement: the product tile appears for orgs entitled on ANY of these real catalog
		// codes — CAMERAS (legacy code; still what's actually granted to existing camera orgs today),
		// VISION (its catalog-corrections replacement, nested under ACCESS — the "Cameras" tier), or
		// VISION_AI (top-level, standalone — the "Hub" tier). Hub-only surfaces, when built, gate on
		// VISION_AI specifically so a Cameras-tier org doesn't see them.
		catalogCodesAny: ['CAMERAS', 'VISION', 'VISION_AI'],
		kind: 'internal',
		route: '/vision'
	}
];

// Rule enforced in the Products UI: at least one product must stay on.
// A workspace with zero products is a broken workspace.
export const MIN_ACTIVE_PRODUCTS = 1;

// Product setup rules (product-licensing plan v0.22 D45, GB 2026-11-03). Setup only: no licence enforcement, no
// server or flag change, and nothing is ever switched off for the customer. The ONE place these rules live; the
// Products & licences page and the settings group panel both use them through useModuleToggle, so they can't disagree.
// Only top-level products count toward MIN_ACTIVE_PRODUCTS. Bookings (a module of Access, D44) and Visitor
// Management (also inside Access) never do.
export const MIN_ACTIVE_PRODUCT_KEYS = Object.freeze(['access', 'rooms', 'lockers', 'vision']);
export const countsTowardMinActiveProducts = (product) => MIN_ACTIVE_PRODUCT_KEYS.includes(product?.key);

export const BOOKINGS_NEEDS_ACCESS = 'Bookings needs Access. Turn Access on first.';
export const ACCESS_NEEDED_BY_BOOKINGS = 'Turn Bookings off first; Bookings needs Access.';

/**
 * Why a product's switch can't move to `nextEnabled` because of another product, or null. Bookings can't turn on
 * while Access is off; Access can't turn off while Bookings is on; Bookings off is always allowed.
 * @param {{key: string}} product
 * @param {boolean} nextEnabled
 * @param {Object<string, boolean>} enabledByKey  product key → currently on
 * @returns {string|null}
 */
export const productDependencyReason = (product, nextEnabled, enabledByKey) => {
	if (product?.key === 'bookings' && nextEnabled && enabledByKey?.access !== true) return BOOKINGS_NEEDS_ACCESS;
	if (product?.key === 'access' && !nextEnabled && enabledByKey?.bookings === true) return ACCESS_NEEDED_BY_BOOKINGS;
	return null;
};

// Every consumer that renders/derives a switchable DASHBOARD list (ProductSwitcher, Sidebar's
// current-product resolution) must exclude inSwitcher:false entries the same way — Settings >
// Products is the one place that keeps using PRODUCTS unfiltered. Defined once here, alongside
// PRODUCTS itself, rather than in either consuming component.
export const isSwitchable = (p) => p.inSwitcher !== false;
export const SWITCHABLE_PRODUCTS = PRODUCTS.filter(isSwitchable);

/**
 * The Partner launcher tile — DELIBERATELY NOT in PRODUCTS. Partner is a USER-scoped identity, not a
 * per-org module, so it must never pass through selectActiveModules (which filters on moduleFlag +
 * entitlement — a tile with no moduleFlag would be filtered out). The switcher/chooser APPEND this
 * separately, gated on selectIsPartner (via usePartnerLauncher). External → opens the standalone app.
 */
export const PARTNER_PRODUCT = {
	key: 'partner',
	label: 'Partner',
	description: 'Your cross-org partner workspace — customers, the devices you service, grants, and your team.',
	logo: <ZezamiiIcon name='shield-check' size={40} />,
	// A bare stroke glyph beside four solid product tiles: no tile, and it inherits the switcher's
	// idle grey, so on the black shell it was a dark outline on a dark ground. `monoTile` asks the
	// switcher for a surface-inverted chip so it sits in the same visual register as the others.
	monoTile: true,
	kind: 'external',
	open: openPartner
};

/**
 * Staff-only launcher for the in-dashboard Partner Admin tool — also DELIBERATELY NOT in PRODUCTS (no
 * moduleFlag; user-scoped). The switcher/chooser APPEND it gated on selectIsPlatformDeveloper (the same
 * developerOnly gate as the /partner-admin route), so Zezamii staff get a discoverable entry instead of
 * a URL. INTERNAL navigation: partner-admin lives under the Access dashboard, so `dashboardKey:'access'`
 * switches the shell to Access before navigating (its own key isn't a valid dashboard). Distinct from the
 * partner-facing PARTNER_PRODUCT tile (which opens the standalone app for actual partners).
 */
export const PARTNER_ADMIN_PRODUCT = {
	key: 'partner-admin',
	label: 'Partner Admin',
	description: 'Zezamii staff tool — create partner companies, invite master accounts, and manage partner teams.',
	logo: <ZezamiiIcon name='user' size={40} />,
	monoTile: true,
	kind: 'internal',
	route: '/access/partner-admin',
	dashboardKey: 'access'
};
